import React, { SyntheticEvent, useState } from 'react';
import { Button, FormField, Header, Icon, Input, Modal, ModalActions, ModalContent, Popup, Select } from 'semantic-ui-react';
import { toast } from 'react-toastify';
import { createOrderPayment } from '../../../services/orders.service';
import { useAppContext } from '../../../hooks/AppProvider';
import { PAYMENT_TYPES_OPTIONS } from '../../../utils/dropdownOptions';
import OrderPayment from '../../../types/entities/OrderPayment';
import { isValidPaymentAmount } from '../../../utils/numberUtils';

interface Props {
    id: string | undefined;
    type?: string;
    disabled?: boolean;
    setOrderPayment: (order: OrderPayment) => void;
}

const UploadOrderPaymentModal = (props: Props) => {

    const { updateParentState } = useAppContext();
    
    const [ openModal, setOpenModal ] = useState(false);

    const [ file, setFile ] = useState<Blob | undefined>(undefined);
    const [ fileName, setFileName ] = useState("");

    const [ requestData, setRequestData ] = useState({
        amount: "",
        type: props.type || "",
    });
    const paymentAmountIsValid = isValidPaymentAmount(requestData.amount, requestData.type);

    const handleOpenModal = (event: SyntheticEvent) => {
        event.preventDefault();
        setOpenModal(true);
    };

    const handleCancelModal = (event: SyntheticEvent) => {
        event.preventDefault();
        setOpenModal(false);
    };

    const handleChange = (e: any, { name, value }: any) => {
        setRequestData({
            ...requestData,
            [name]: value ?? "",
        });
    };

    const handleChangePaymentAmount = (e: any, { name, value }: any) => {
        const newValue = value.replace(/[^0-9.,]/g, '').replace(/,/g, '');
        if (newValue.length <= 10) {
            setRequestData({
                ...requestData,
                [name]: formatMoney(newValue) ?? "",
            });
        }
    };

    // this method receives a string of numbers and returns a formatted string
    const formatMoney = (value: string | undefined) => {
        if (!value) {
            return "";
        }
        return value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    const handleSubmit = async (event: SyntheticEvent, orderId: string | undefined) => {
        event.preventDefault();
        updateParentState({ loading: true });
        try {

            if (orderId === undefined) {
                throw new Error("El id del pedido no puede ser undefined.");
            }

            if (!requestData.type) {
                throw new Error("Favor de seleccionar un tipo de pago.");
            }

            if (!paymentAmountIsValid) {
                throw new Error(requestData.type === "INSTALLMENT"
                    ? "El monto del abono debe ser mayor a $0."
                    : "El monto del pago debe ser igual o mayor a $0.");
            }

            if (file === undefined) {
                throw new Error("Favor de seleccionar un comprobante de pago.");
            }

            const formData = new FormData();
            formData.append( "paymentProof", file, fileName );
            // clean remaining money after creating the order
            formData.append( "amount", requestData.amount?.replace(/[^0-9.,]/g, '').replace(/,/g, '') );
            formData.append( "type", requestData.type );

            const response = await createOrderPayment(orderId, formData);
            if (response?.status === 201) {
                props.setOrderPayment(response?.data);
                toast.success("El pago se ha registrado con exito.");
                setOpenModal(false);
            } else {
                throw new Error("Ha ocurrido un error al registrar el pago.");
            }
        } catch (error: any) {
            toast.error(error.message);
        } finally {
            updateParentState({ loading: false });
        }
    };

    const handlePaymentProof = async (event: any) => {
        event.preventDefault();
        try {
            const file = event.target.files[0];
            setFile(file);
            setFileName(file.name);
        } catch (error: any) {
            toast.error(error.message);
        }
    };

    return (
        <>
            <Modal
                closeOnDimmerClick
                closeIcon={true}
                size="small"
                onClose={(event) => handleCancelModal(event)}
                open={openModal}
                trigger={
                    <Popup
                        trigger={
                            <Button color="orange" icon disabled={props.disabled} onClick={ (event) => handleOpenModal(event) } >
                                <Icon name="upload"/> {props.type === "ADVANCE" ? "Cargar anticipo" : "Registrar nuevo pago"}
                            </Button>
                        }
                        content='Registrar nuevo pago'
                        position='bottom center'
                        size='small'
                        inverted
                    />
                } >
                <Header icon="upload" content='Registrar nuevo pago' />
                <ModalContent>
                    <p>
                        Asegurate de que el monto sea el correcto y que el comprobante de pago sea valido.
                    </p>

                    <div className="ui form">

                        <FormField
                            control={Input}
                            label={<label htmlFor="form-select-control-installer">Monto del pago</label>}
                            placeholder="0.00"
                            name="amount"
                            value={requestData.amount}
                            onChange={handleChangePaymentAmount}
                            error={requestData.amount !== "" && !paymentAmountIsValid}
                            required
                        />

                        <FormField
                            clearable
                            control={Select}
                            label={<label htmlFor="form-select-control-payment">Tipo de pago</label>}
                            placeholder="Selecciona una opcion"
                            name="type"
                            value={requestData.type}
                            onChange={handleChange}    
                            options={PAYMENT_TYPES_OPTIONS}                                                      
                            required
                            disabled={props.type !== undefined}
                        />

                        <FormField>
                            <label htmlFor="payment-proof">Adjuntar comprobante de pago</label>
                            <input id='payment-proof' type='file' accept="*" onChange={event => handlePaymentProof(event)} />
                        </FormField>
                    </div>
                </ModalContent>
                <ModalActions>
                    <Button onClick={ (event) => handleCancelModal(event) }>
                        <Icon name="cancel" /> Cancelar
                    </Button>
                    <Button color="green" onClick={ (event) => handleSubmit(event, props.id) }>
                        <Icon name="checkmark" /> Aceptar
                    </Button>
                </ModalActions>
            </Modal>
        </>
    );
};

export default UploadOrderPaymentModal;
